import './item.comp.css'

import React, { CSSProperties, useMemo, useState } from 'react'
import clsx from 'clsx'
import rehypeSanitize from 'rehype-sanitize'
import { Descendant } from 'slate'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  BuilderMenu,
  DefaultVariableResult,
  IActionRepeaterValue,
  isValidUrl,
  TypographyGroupValue,
  TypographySelectValue,
  VariableSelectFeatureFlag,
} from '@makyoapp/makyo-common'
import {
  CardItem,
  ColorVariant,
  CornerRadius,
  getHarmoniousLight,
  getTypographyTokenStyle,
  isHexColor,
  MaterialIcon,
  RawSpacingInput,
  TypographyValue,
  useMtwContext,
} from '@makyoapp/makyo-tw-ui'
import { HoverEffectVariant } from '@makyoapp/makyo-tw-ui/dist/lib/utils/hover-effects'

import { IViewComponent } from '@/core/app-builder/app/component'
import { CoreComponentAction } from '@/core/models/app-components/types'
import { LookupCell } from '@/core/models/columns/lookup'
import { IRow } from '@/core/models/tables/row'
import { cn } from '@/lib/cn'
import { MarkdownPreview } from '@/lib/markdown'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useCompBackgroundColor } from '@/context/__project__/CompContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useCheckVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { getBorderRadius, isValidColor } from '@/shared/brand-kit'
import { BuilderComponentSettingValidation, ComponentSettingKey } from '@/shared/builder'
import { LeadingSidebarTab } from '@/shared/builder-ui'
import { getValidationFn, parseSpacing, twProse } from '@/shared/builder-utils'
import { AspectRatio, MdShape, MediaType } from '@/shared/builder/types'
import { SpacingInputValue } from '@/shared/builder/types/spacing-input'
import { THUMB_AVATAR, THUMB_SRC } from '@/shared/constants'
import { getSafely } from '@/shared/safely'
import { cleanUpText, parseLabel } from '@/shared/utils'

import { CellComponent } from '../../shared/CellComponent'
import { CompOverlayDevmode } from '../../shared/CompOverlayDevmode'
import { CompVariableSelect } from '../../shared/CompVariableSelect'
import { ColBrief } from '../../utils'
import { useGetBadgeContent } from '../list/useGetBadgeContent'

const getYouTubeEmbedUrl = (url: string) => {
  let videoId

  if (url.includes('youtube.com/watch?v=')) {
    videoId = new URL(url).searchParams.get('v')
  } else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1].split('?')[0]
  } else if (url.includes('youtube.com/shorts/')) {
    videoId = url.split('youtube.com/shorts/')[1].split('?')[0]
  }

  return videoId ? `https://www.youtube.com/embed/${videoId}` : ''
}

export interface BaseCompCardItemProps {
  data: IRow
  tableId: string
  rowIndex: number
  settings: Record<string, any>
  footerLeading?: {
    icon?: string
    actions?: IActionRepeaterValue[]
    end?: boolean
    typography?: TypographyValue
  }
  footerTrailing?: {
    icon?: string
    actions?: IActionRepeaterValue[]
    end?: boolean
    typography?: TypographyValue
  }
  mediaRatio?: AspectRatio
  mediaCorner?: MdShape
  shape?: MdShape
  numRow: number
  orientation?: string
  thumbCol?: ColBrief
  headerCol?: ColBrief
  subheaderCol?: ColBrief
  mediaCol?: ColBrief
  titleCol?: ColBrief
  subtitleCol?: ColBrief
  supportingTextCol?: ColBrief
  metadataTypo?: TypographySelectValue
  actionHelpTypo?: TypographySelectValue
  headerTypo?: TypographySelectValue
  subheaderTypo?: TypographySelectValue
  titleTypo?: TypographySelectValue
  subtitleTypo?: TypographySelectValue
  supportingTextTypo?: TypographySelectValue
  textLine1Limit?: number
  textLine2Limit?: number
  textLine3Limit?: number
  textLine4Limit?: number
  textLine5Limit?: number
  textLine6Limit?: number
  itemGap?: SpacingInputValue
  itemPadding?: SpacingInputValue
  maxHeightPerItem?: number
  stacked?: boolean
  onMaxHeightChange?: (height: number) => void
  onStackedChange?: (stacked: boolean) => void
  equalHeight?: boolean
}

export interface CompCardItemProps extends BaseCompCardItemProps {
  settings: Record<string, any>
  containerClient: any
  action: CoreComponentAction
  component: IViewComponent
  trailingMenu?: BuilderMenu
  trailingHeaderMenu?: BuilderMenu
  leadingBtnStyles?: CSSProperties
  trailingBtnStyles?: CSSProperties
  currentParentBg?: ColorVariant
}

/**
 * CompCardItemCore
 *
 */
export const CompCardItemCore = React.memo(
  ({
    tableId,
    rowIndex,
    footerLeading,
    footerTrailing,
    data,
    thumbCol,
    headerCol,
    subheaderCol,
    mediaCol,
    titleCol,
    subtitleCol,
    supportingTextCol,
    mediaCorner,
    mediaRatio,
    shape,
    orientation,
    metadataTypo,
    actionHelpTypo,
    component,
    action: { simulateAction },
    containerClient,
    settings,
    trailingMenu,
    trailingHeaderMenu,
    headerTypo,
    subheaderTypo,
    titleTypo,
    subtitleTypo,
    supportingTextTypo,
    textLine1Limit,
    textLine2Limit,
    textLine3Limit,
    textLine4Limit,
    textLine5Limit,
    textLine6Limit,
  }: CompCardItemProps) => {
    // access global context
    const { currentParentBg } = useCompBackgroundColor()
    const { currentUser } = useAppAuthContext()
    const { getBadgeContent } = useGetBadgeContent()
    const { globalDoc } = useMtwContext()
    const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

    // local state
    const [imageError, setImageError] = useState(false)

    // ========================= data =========================
    const header = cleanUpText(data[headerCol?._id as string]?.toString())
    const title = data[titleCol?._id as string]?.toString()
    const description = data[subheaderCol?._id as string]?.toString()
    const supportingText = data[supportingTextCol?._id as string]?.toString()
    const subtitle = data[subtitleCol?._id as string]?.toString()
    const media = data[mediaCol?._id as string]?.value || 'star'
    const itemShape = settings[ComponentSettingKey.CardDesignShape] as MdShape
    const footerMargin = settings[ComponentSettingKey.CardDesignFooterMargin] as RawSpacingInput
    const leadingButtonPadding = settings[ComponentSettingKey.CardDesignLeadingButtonPadding] as RawSpacingInput
    const trailingButtonPadding = settings[ComponentSettingKey.CardDesignTrailingButtonPadding] as RawSpacingInput
    const footerGap = (settings[ComponentSettingKey.CardDesignFooterGap]?.value as SpacingInputValue) || 0

    const mediaAlignment = settings[ComponentSettingKey.CardDesignMediaAlignment] as string

    const mediaSrc = useMemo(() => {
      if (!mediaCol) {
        return undefined
      }

      if (typeof media !== 'string') {
        if (Array.isArray(media)) {
          if (media.length === 1) {
            return media[0]?.toString() || THUMB_SRC
          }
          if (media.length === 0 && mediaCol._Type !== ColumnTypes.Icon) {
            return THUMB_SRC
          }
        }
      }

      if (mediaCol._Type === ColumnTypes.Barcode || mediaCol._Type === ColumnTypes.QRCode) {
        return data[mediaCol?._id as string]?.toString()
      }

      if (typeof media === 'string') {
        if (media && !imageError) {
          return media || THUMB_SRC
        } else {
          return mediaCol._Type !== ColumnTypes.Icon ? THUMB_SRC : ''
        }
      }
    }, [mediaCol, media, data, imageError])

    let mediaType = MediaType.Image
    let embed = undefined
    if (
      media &&
      typeof media === 'string' &&
      mediaCol?._Type === ColumnTypes.URL &&
      !media.includes('img.youtube.com')
    ) {
      if (!getValidationFn(BuilderComponentSettingValidation.VideoFile)(media)) {
        const isEmbed = ['youtu.be', 'youtube.com', 'vimeo.com', 'soundcloud.com', 'giphy.com'].some((em) =>
          media.includes(em)
        )

        if (isEmbed) {
          let embedUrl = media

          embedUrl = embedUrl.includes('youtube.com/shorts') ? embedUrl.replace(/shorts\//g, 'watch?v=') : embedUrl

          if (embedUrl && embedUrl.includes('?feature=share')) {
            embedUrl = embedUrl.replace('?feature=share', '')
            embedUrl = embedUrl.replace('youtube.com/watch?v=', 'youtu.be/')
          }

          if (embedUrl && embedUrl.includes('www.youtube.com/watch?v=')) {
            embedUrl = embedUrl.replace('www.youtube.com/watch?v=', 'youtu.be/')
          }

          const embedShape = getBorderRadius(shape)

          const embedStyles = {
            aspectRatio: mediaRatio && mediaRatio !== AspectRatio.Auto ? mediaRatio.replace(':', '/') : undefined,
            '--_container-shape': `var(--md-sys-shape-corner-${mediaCorner})`,
            borderRadius: 'var(--_container-shape)',
            clipPath: `inset(0 0 0 0 round ${embedShape} ${embedShape} 0 0)`,
            width: '100%',
            height: 'auto',
            position: 'relative',
            overflow: 'hidden',
          } as CSSProperties

          embed = media ? (
            <div slot='media' className='embed-responsive' style={embedStyles}>
              <iframe
                src={getYouTubeEmbedUrl(embedUrl)}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 0,
                }}
                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                allowFullScreen
                title='YouTube video player'
              />
            </div>
          ) : undefined
          mediaType = MediaType.Embed
        } else {
          mediaType = MediaType.Video
        }
      }
    }

    if (mediaCol) {
      const cell = data?.[mediaCol._id]

      if (mediaCol._Type === ColumnTypes.Icon) {
        mediaType = MediaType.Icon
      }

      if (mediaCol._Type === ColumnTypes.Lookup && cell instanceof LookupCell) {
        const colType = cell.resolvedType
        if (colType === ColumnTypes.Icon) {
          mediaType = MediaType.Icon
        } else if (colType === ColumnTypes.Image) {
          mediaType = MediaType.Image
        }
      }
    }

    const actionHelp = settings[ComponentSettingKey.CardDataActionHelp] as string

    const leadingLabel = useParseVariableText(
      settings[ComponentSettingKey.CardDataLeadingActionLabel] as Descendant[],
      containerClient,
      tableId,
      rowIndex
    )

    const trailingLabel = useParseVariableText(
      settings[ComponentSettingKey.CardDataTrailingActionLabel] as Descendant[],
      containerClient,
      tableId,
      rowIndex
    )

    const footerLeadingActionEnabled = Boolean(settings[ComponentSettingKey.CardDataLeadingAction])
    const footerLeadingActionIconPosition = settings[ComponentSettingKey.CardDataLeadingActionIconPosition]
    const footerLeadingActions = settings[ComponentSettingKey.CardActionLeadingClick] as IActionRepeaterValue[]

    const footerTrailingActionEnabled = Boolean(settings[ComponentSettingKey.CardDataTrailingAction])
    const footerTrailingActionIconPosition = settings[ComponentSettingKey.CardDataTrailingActionIconPosition]
    const footerTrailingActions = settings[ComponentSettingKey.CardActionTrailingClick] as IActionRepeaterValue[]

    const typoSettings = settings[ComponentSettingKey.CardDesignTypography] as TypographyGroupValue
    const footerLeadingTypography = typoSettings[
      ComponentSettingKey.CardDesignLeadingButtonTypography
    ] as TypographyValue
    const footerTrailingTypography = typoSettings[
      ComponentSettingKey.CardDesignTrailingButtonTypography
    ] as TypographyValue

    const leadingShape = settings[ComponentSettingKey.CardDesignLeadingButtonShape] as MdShape

    const trailingShape = settings[ComponentSettingKey.CardDesignTrailingButtonShape] as MdShape

    const getLeadingButtonFullWidth = settings[ComponentSettingKey.CardDesignLeadingButtonFullWidth]
    const getTrailingButtonFullWidth = settings[ComponentSettingKey.CardDesignTrailingButtonFullWidth]

    const footerButtonOrientation = settings[ComponentSettingKey.CardDesignFooterButtonOrientation]

    const leadingButtonFullWidth = footerButtonOrientation === 'horizontal' ? 'horizontal' : getLeadingButtonFullWidth
    const trailingButtonFullWidth = footerButtonOrientation === 'horizontal' ? 'horizontal' : getTrailingButtonFullWidth

    // ========================= design =========================

    const bgColor = settings[ComponentSettingKey.CardDesignBgColor]
    const leadingButtonColor = settings[ComponentSettingKey.CardDesignLeadingButtonScheme]
    const trailingButtonColor = settings[ComponentSettingKey.CardDesignTrailingButtonScheme]
    const bgOpacity = getSafely(settings, [ComponentSettingKey.CardDesignBgOpacity], [100])[0] ?? 100

    const backgroundColorPage = containerClient?.settings._Settings._BackgroundColor

    const headerAvatarShape = settings[ComponentSettingKey.CardDesignThumbShape] as MdShape
    const headerTitleAlign = settings[ComponentSettingKey.CardDesignHeaderTitleAlignment] as string
    const headerSubtitleAlign = settings[ComponentSettingKey.CardDesignHeaderSubtitleAlignment] as string
    const titleAlign = settings[ComponentSettingKey.CardDesignTitleAlignment] as string
    const subtitleAlign = settings[ComponentSettingKey.CardDesignSubtitleAlignment] as string
    const supportingTextAlign = settings[ComponentSettingKey.CardDesignSupportingTextAlignment] as string

    const padding = (settings[ComponentSettingKey.CardDesignPadding] as RawSpacingInput) ?? {
      top: 12,
      right: 16,
      bottom: 12,
      left: 16,
    }

    // Text Lines
    const mediaMargin = parseSpacing(
      settings[ComponentSettingKey.CardDesignMediaMargin] as SpacingInputValue,
      mediaType === MediaType.Icon
    )

    const metaAlign = settings[ComponentSettingKey.CardDesignMetaAlignment] as string
    const actionAlign = settings[ComponentSettingKey.CardDesignActionAlignment]

    const metaTypoStyle = getTypographyTokenStyle(metadataTypo as TypographySelectValue)

    const thumbIconVariant = settings[ComponentSettingKey.CardDesignIconVariant] ?? 'standard'
    const thumbIconColorVariant = settings[ComponentSettingKey.CardDesignIconColorVariant] ?? 'primary-20'
    const isThumbIconColorDynamic = Boolean(settings[ComponentSettingKey.CardDesignIconColorCustom])
    const thumbIconBackgroundColorVariant =
      settings[ComponentSettingKey.CardDesignIconBackgroundColorVariant] ?? 'primary-80'
    const thumbIconColorCol = settings[ComponentSettingKey.CardDesignIconColorColumn] as string
    const thumbIconBackgroundColorCol = settings[ComponentSettingKey.CardDesignIconBackgroundColorColumn] as string

    const mediaIconVariant = settings[ComponentSettingKey.CardDesignMediaIconVariant] ?? 'standard'
    const mediaIconColorVariant = settings[ComponentSettingKey.CardDesignMediaIconColorVariant] as string
    const mediaIconBackgroundColorCol = settings[ComponentSettingKey.CardDesignMediaIconBackgroundColorColumn] as string
    const mediaIconColorCol = settings[ComponentSettingKey.CardDesignMediaIconColorColumn] as string
    const isMediaIconColorDynamic = Boolean(settings[ComponentSettingKey.CardDesignMediaIconColorCustom])
    const mediaIconBackgroundColorVariant =
      settings[ComponentSettingKey.CardDesignMediaIconBackgroundColorVariant] ?? 'primary-80'

    const thumbIconBackgroundColorHex = globalDoc?.documentElement
      ? getComputedStyle(globalDoc.documentElement!).getPropertyValue(`--color-${thumbIconBackgroundColorVariant}`)
      : undefined

    const getFinalThumbIconBackgroundColor = (row: IRow) => {
      if (isThumbIconColorDynamic && thumbIconBackgroundColorCol) {
        const color = row[thumbIconBackgroundColorCol]?.toString()
        return isValidColor(color) ? color : thumbIconBackgroundColorHex
      }
      return thumbIconBackgroundColorHex
    }

    const thumbIconColorHex = globalDoc?.documentElement
      ? getComputedStyle(globalDoc.documentElement!).getPropertyValue(`--color-${thumbIconColorVariant}`)
      : undefined

    const getFinalThumbIconColor = (row: IRow) => {
      if (isThumbIconColorDynamic && thumbIconColorCol) {
        const color = row[thumbIconColorCol]?.toString()
        return isValidColor(color) ? color : thumbIconColorHex
      }
      return thumbIconColorHex
    }

    const mediaIconBackgroundColorHex = globalDoc?.documentElement
      ? getComputedStyle(globalDoc.documentElement!).getPropertyValue(`--color-${mediaIconBackgroundColorVariant}`)
      : undefined

    const mediaIconColorHex = globalDoc?.documentElement
      ? getComputedStyle(globalDoc.documentElement!).getPropertyValue(`--color-${mediaIconColorVariant}`)
      : undefined

    const getFinalMediaIconBackgroundColor = (row: IRow) => {
      if (isMediaIconColorDynamic && mediaIconBackgroundColorCol) {
        const color = row[mediaIconBackgroundColorCol]?.toString()
        return isValidColor(color) ? color : mediaIconBackgroundColorHex
      }
      return mediaIconBackgroundColorHex
    }

    const getFinalMediaIconColor = (row: IRow) => {
      if (isMediaIconColorDynamic && mediaIconColorCol) {
        const color = row[mediaIconColorCol]?.toString()
        return isValidColor(color) ? color : mediaIconColorHex
      }
      return mediaIconColorHex
    }

    const getColorHex = (iconColor: string) =>
      isHexColor(iconColor)
        ? iconColor
        : globalDoc?.documentElement
          ? getComputedStyle(globalDoc.documentElement!).getPropertyValue(`--color-${iconColor}`)
          : undefined

    const getIconStyleByType = (iconType: string, iconColor: string, backgroundColor: string) => {
      const hexColor = getColorHex(iconColor)

      const lightBg = backgroundColor

      const backgroundColorStyle = isMediaIconColorDynamic && hexColor ? lightBg : backgroundColor // `var(--color-${backgroundColor})`

      switch (iconType) {
        case 'standard':
          return {
            color: hexColor ? hexColor : 'inherit',
          }
        case 'filled':
          return {
            backgroundColor: backgroundColorStyle,
            color: hexColor ? hexColor : backgroundColor, //`var(--color-${backgroundColor})`,
          }
        case 'outlined':
          return {
            border: `1px solid ${hexColor ? hexColor : `var(--color-on-${backgroundColor})`}`,
            backgroundColor: hexColor ? lightBg : `var(--color-on-${backgroundColor})`,
            color: hexColor ? hexColor : `var(--color-on-${backgroundColor})`,
          }
      }
    }

    const mediaIconSize = settings[ComponentSettingKey.CardDesignMediaIconSize]?.[0]

    const equalHeight = Boolean(settings[ComponentSettingKey.CardDesignSameHeight])

    // ========================= visibility =========================

    // TODO: show menu based on visibility
    const { checkVisibility } = useCheckVisibilityV2()

    // ========================= elements ============================

    const meta1Setting = useMemo(
      () => (settings[ComponentSettingKey.CardDataMetadata1] as DefaultVariableResult[]) || [],
      [settings]
    )
    const meta1Margin = parseSpacing(settings[ComponentSettingKey.CardDesignMetaMargin] as SpacingInputValue) || {}
    const meta2Setting = useMemo(
      () => (settings[ComponentSettingKey.CardDataMetadata2] as DefaultVariableResult[]) || [],
      [settings]
    )

    const headerContent = useMemo(() => {
      if (!headerCol) return null

      if (headerCol._Type === ColumnTypes.RichText) {
        return (
          <MarkdownPreview
            className='prose'
            style={{
              ...twProse(),
              font: 'inherit !important',
            }}
            source={header}
            rehypePlugins={[[rehypeSanitize]]}
          />
        )
      }

      return (
        <div key='header'>
          <CellComponent cell={data[headerCol._id]} type={headerCol._Type} />
        </div>
      )
    }, [headerCol, data, header])

    const descriptionContent = useMemo(() => {
      if (!subheaderCol) return null

      if (subheaderCol._Type === ColumnTypes.RichText) {
        return (
          <MarkdownPreview
            className='prose'
            style={{
              ...twProse(),
              font: 'inherit !important',
            }}
            source={description}
            rehypePlugins={[[rehypeSanitize]]}
          />
        )
      }

      return (
        <div key='description'>
          <CellComponent cell={data[subheaderCol._id]} type={subheaderCol._Type} />
        </div>
      )
    }, [subheaderCol, data, description])

    const titleContent = useMemo(() => {
      if (!titleCol) return null

      if (titleCol._Type === ColumnTypes.RichText) {
        return (
          <MarkdownPreview
            className='prose'
            style={{
              ...twProse(),
              font: 'inherit !important',
            }}
            source={title}
            rehypePlugins={[[rehypeSanitize]]}
          />
        )
      }

      return (
        <div key='title'>
          <CellComponent cell={data[titleCol._id]} type={titleCol._Type} />
        </div>
      )
    }, [titleCol, data, title])

    const subtitleContent = useMemo(() => {
      if (!subtitleCol) return null

      if (subtitleCol._Type === ColumnTypes.RichText) {
        return (
          <MarkdownPreview
            className='prose'
            style={{
              ...twProse(),
              font: 'inherit !important',
            }}
            source={subtitle}
            rehypePlugins={[[rehypeSanitize]]}
          />
        )
      }

      return (
        <div key='subtitle'>
          <CellComponent cell={data[subtitleCol._id]} type={subtitleCol._Type} />
        </div>
      )
    }, [subtitleCol, data, subtitle])

    const supportingTextContent = useMemo(() => {
      if (!supportingTextCol) return null

      if (supportingTextCol._Type === ColumnTypes.RichText) {
        return (
          <MarkdownPreview
            className='prose'
            style={{
              ...twProse(),
              font: 'inherit !important',
            }}
            source={supportingText}
            rehypePlugins={[[rehypeSanitize]]}
          />
        )
      }

      if (supportingTextCol._Type === ColumnTypes.Rating) {
        return (
          <div key='supporting-text'>
            <CellComponent cell={data[supportingTextCol._id]} type={supportingTextCol._Type} />
          </div>
        )
      }

      return <div>{supportingText}</div>
    }, [supportingTextCol, data, supportingText])

    const content = useMemo(() => {
      let els = []
      if (titleCol) {
        els.push(titleContent)
      }

      if (subtitleCol) {
        els.push(subtitleContent)
      }

      if (supportingTextCol) {
        els.push(supportingTextContent)
      }

      return els
    }, [titleCol, subtitleCol, supportingTextCol, titleContent, subtitleContent, supportingTextContent])

    const itemClickActions = settings[ComponentSettingKey.CardActionItemClick] as IActionRepeaterValue[]

    const handleCardClick = () => {
      openComponentSettings(component)
      if (isDevelopmentMode) return
      if (itemClickActions && itemClickActions.length > 0) {
        simulateAction({
          actions: itemClickActions,
          rowIndex,
          rowId: data._id,
          tableId,
        })
      }
    }

    const getLineClampStyles = (lineLimit: number | undefined) => {
      return lineLimit
        ? {
            WebkitLineClamp: lineLimit,
            lineClamp: lineLimit,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }
        : {}
    }

    const renderMetaData = () => {
      if (!!meta1Setting.length || !!meta2Setting.length) {
        return (
          <div
            className={clsx('flex items-center gap-2', content && 'pt-4', metaAlign && `justify-${metaAlign}`)}
            style={
              {
                ...metaTypoStyle,
                ...meta1Margin,
              } as CSSProperties
            }
            slot='meta'
          >
            {!!meta1Setting.length && (
              <div
                className='flex items-center gap-1'
                style={{ ...getLineClampStyles(textLine6Limit) } as CSSProperties}
              >
                <CompVariableSelect items={meta1Setting} rowId={data._id} tableId={tableId} />
              </div>
            )}
            {!!meta2Setting.length && (
              <div
                className='flex items-center gap-1'
                style={{ ...getLineClampStyles(textLine6Limit) } as CSSProperties}
              >
                <CompVariableSelect items={meta2Setting} tableId={tableId} rowId={data._id} />
              </div>
            )}
          </div>
        )
      }
      return null
    }

    if (!data) return null

    const noMedia = !thumbCol && !mediaCol
    const noHeader = !headerCol && !header && !subheaderCol
    const noContent = !content && !supportingTextCol && !titleCol && !subtitleCol && !embed

    if (noMedia && noHeader && noContent) return null

    const getAvatar = () => {
      if (!thumbCol) return undefined

      let avatarType = thumbCol._Type
      let avatarCell = data[thumbCol._id]
      let avatarValue = avatarCell?.value ?? THUMB_SRC

      if (avatarType === ColumnTypes.Lookup && avatarCell && avatarCell instanceof LookupCell) {
        avatarType = avatarCell.resolvedType

        if (
          Array.isArray(avatarValue) &&
          avatarValue.length > 0 &&
          !!avatarValue[0] &&
          typeof avatarValue[0] === 'string'
        ) {
          avatarValue = avatarCell.value[0] as string
          if (isValidUrl(avatarValue)) {
            avatarType = ColumnTypes.Image
          } else {
            avatarType = ColumnTypes.Icon
          }
        }
      }

      return {
        type: avatarType,
        value: avatarValue,
      }
    }

    const renderMenuItems = (row: IRow, type: 'trailing' | 'header') => {
      const menu = type === 'trailing' ? trailingMenu : trailingHeaderMenu

      if (!menu) return []

      return menu.items
        .filter((item) => checkVisibility({ visibility: item.visibility }))
        .map((menuItem) => {
          const isActionButtonsHaveIconOrImage = menu?.items.some((m) => {
            const isIcon = m?.icon?.flag === VariableSelectFeatureFlag.Icon
            const isAvatar = m?.icon?.flag === VariableSelectFeatureFlag.Avatar
            const isImage = m?.icon?.flag === VariableSelectFeatureFlag.Image
            return isIcon || isAvatar || isImage
          })

          const isIcon = menuItem?.icon?.flag === VariableSelectFeatureFlag.Icon
          const isAvatar = menuItem?.icon?.flag === VariableSelectFeatureFlag.Avatar
          const isImage = menuItem?.icon?.flag === VariableSelectFeatureFlag.Image

          let media: { icon?: React.ReactNode | undefined; image?: string | undefined } = {
            icon: isActionButtonsHaveIconOrImage ? <div className='w-6 h-6 visible-hidden' /> : undefined,
            image: undefined,
          }

          if (isIcon) {
            media = { icon: <MaterialIcon icon={(menuItem.icon as any)?.value} size={24} /> }
          } else if (isAvatar) {
            media = { image: currentUser?.photo || THUMB_AVATAR, icon: undefined }
          } else if (isImage) {
            media = { image: (menuItem.icon as any)?.value, icon: undefined }
          }

          const badge = getBadgeContent(menuItem)

          let theBadge = undefined

          if (badge.showBadge) {
            if (badge.badgeSize === 's') {
              theBadge = null
            } else {
              theBadge = badge.badgeValue
            }
          }

          return {
            ...media,
            id: menuItem.id,
            label: parseLabel(menuItem.title),
            badgeText: theBadge,
            type: 'default' as const,
            onClick: () => {
              if (menuItem.actions && menuItem.actions.length > 0) {
                simulateAction({
                  actions: menuItem.actions,
                  rowIndex: rowIndex,
                  tableId: tableId,
                  rowId: row._id as string,
                })
              }
            },
          }
        })
    }

    const text1Color = settings[ComponentSettingKey.CardDesignText1Color]
    const text2Color = settings[ComponentSettingKey.CardDesignText2Color]
    const text3Color = settings[ComponentSettingKey.CardDesignText3Color]
    const text4Color = settings[ComponentSettingKey.CardDesignText4Color]
    const text5Color = settings[ComponentSettingKey.CardDesignText5Color]

    const text1ColorSolid = settings[ComponentSettingKey.CardDesignText1ColorSolid]
    const text2ColorSolid = settings[ComponentSettingKey.CardDesignText2ColorSolid]
    const text3ColorSolid = settings[ComponentSettingKey.CardDesignText3ColorSolid]
    const text4ColorSolid = settings[ComponentSettingKey.CardDesignText4ColorSolid]
    const text5ColorSolid = settings[ComponentSettingKey.CardDesignText5ColorSolid]

    const text1ColorStartGradient = settings[ComponentSettingKey.CardDesignText1ColorStartGradient]
    const text2ColorStartGradient = settings[ComponentSettingKey.CardDesignText2ColorStartGradient]
    const text3ColorStartGradient = settings[ComponentSettingKey.CardDesignText3ColorStartGradient]
    const text4ColorStartGradient = settings[ComponentSettingKey.CardDesignText4ColorStartGradient]
    const text5ColorStartGradient = settings[ComponentSettingKey.CardDesignText5ColorStartGradient]

    const text1ColorEndGradient = settings[ComponentSettingKey.CardDesignText1ColorEndGradient]
    const text2ColorEndGradient = settings[ComponentSettingKey.CardDesignText2ColorEndGradient]
    const text3ColorEndGradient = settings[ComponentSettingKey.CardDesignText3ColorEndGradient]
    const text4ColorEndGradient = settings[ComponentSettingKey.CardDesignText4ColorEndGradient]
    const text5ColorEndGradient = settings[ComponentSettingKey.CardDesignText5ColorEndGradient]

    const text1ColorGradientDirection = settings[ComponentSettingKey.CardDesignText1ColorGradientDirection]
    const text2ColorGradientDirection = settings[ComponentSettingKey.CardDesignText2ColorGradientDirection]
    const text3ColorGradientDirection = settings[ComponentSettingKey.CardDesignText3ColorGradientDirection]
    const text4ColorGradientDirection = settings[ComponentSettingKey.CardDesignText4ColorGradientDirection]
    const text5ColorGradientDirection = settings[ComponentSettingKey.CardDesignText5ColorGradientDirection]

    const metadataColor = settings[ComponentSettingKey.CardDesignMetadataColor]
    const metadataColorSolid = settings[ComponentSettingKey.CardDesignMetadataColorSolid]
    const metadataColorStartGradient = settings[ComponentSettingKey.CardDesignMetadataColorStartGradient]
    const metadataColorEndGradient = settings[ComponentSettingKey.CardDesignMetadataColorEndGradient]
    const metadataColorGradientDirection = settings[ComponentSettingKey.CardDesignMetadataColorGradientDirection]

    const footerAreaColor = settings[ComponentSettingKey.CardDesignFooterAreaColor]
    const footerAreaColorSolid = settings[ComponentSettingKey.CardDesignFooterAreaColorSolid]
    const footerAreaColorStartGradient = settings[ComponentSettingKey.CardDesignFooterAreaColorStartGradient]
    const footerAreaColorEndGradient = settings[ComponentSettingKey.CardDesignFooterAreaColorEndGradient]
    const footerAreaColorGradientDirection = settings[ComponentSettingKey.CardDesignFooterAreaColorGradientDirection]

    const colorMode = settings[ComponentSettingKey.CardDesignColorMode] || 'static'
    const colorToneOrder = settings[ComponentSettingKey.CardDesignColorToneOrder] || 'asc'
    const colorStartPalette = settings[ComponentSettingKey.CardDesignColorStartPalette] || 'primary'

    const hoverEffect = settings[ComponentSettingKey.CardDesignHoverEffect] as HoverEffectVariant

    return (
      <CardItem
        backgroundColor={bgColor}
        backgroundOpacity={bgOpacity}
        showAvatar={!!thumbCol}
        orientation={orientation as 'horizontal' | 'vertical'}
        outlined={false}
        elevated={false}
        handleCardClick={() => handleCardClick()}
        backgroundColorPage={currentParentBg as ColorVariant}
        // header title
        textLine1Alignment={headerTitleAlign as any}
        textLine1Limit={textLine1Limit}
        textLine1Typography={headerTypo as TypographyValue}
        textLine1ColorType={text1Color}
        textLine1ColorSolid={text1ColorSolid}
        textLine1ColorStartGradient={text1ColorStartGradient}
        textLine1ColorEndGradient={text1ColorEndGradient}
        textLine1ColorGradientDirection={text1ColorGradientDirection}
        // header subtitle
        textLine2Alignment={headerSubtitleAlign as any}
        textLine2Limit={textLine2Limit}
        textLine2Typography={subheaderTypo as TypographyValue}
        textLine2ColorType={text2Color}
        textLine2ColorSolid={text2ColorSolid}
        textLine2ColorStartGradient={text2ColorStartGradient}
        textLine2ColorEndGradient={text2ColorEndGradient}
        textLine2ColorGradientDirection={text2ColorGradientDirection}
        // title
        textLine3Typography={titleTypo as TypographyValue}
        textLine3Alignment={titleAlign as any}
        textLine3Limit={textLine3Limit}
        textLine3ColorType={text3Color}
        textLine3ColorSolid={text3ColorSolid}
        textLine3ColorStartGradient={text3ColorStartGradient}
        textLine3ColorEndGradient={text3ColorEndGradient}
        textLine3ColorGradientDirection={text3ColorGradientDirection}
        // subtitle
        textLine4Typography={subtitleTypo as TypographyValue}
        textLine4Alignment={subtitleAlign as any}
        textLine4Limit={textLine4Limit}
        textLine4ColorType={text4Color}
        textLine4ColorSolid={text4ColorSolid}
        textLine4ColorStartGradient={text4ColorStartGradient}
        textLine4ColorEndGradient={text4ColorEndGradient}
        textLine4ColorGradientDirection={text4ColorGradientDirection}
        // supporting text
        textLine5Typography={supportingTextTypo as TypographyValue}
        textLine5Alignment={supportingTextAlign as any}
        textLine5Limit={textLine5Limit}
        textLine5ColorType={text5Color}
        textLine5ColorSolid={text5ColorSolid}
        textLine5ColorStartGradient={text5ColorStartGradient}
        textLine5ColorEndGradient={text5ColorEndGradient}
        textLine5ColorGradientDirection={text5ColorGradientDirection}
        // metadata
        metadataTypography={metadataTypo as TypographyValue}
        metadataAlignment={metaAlign as any}
        metadataLimit={textLine6Limit}
        metadataColorType={metadataColor}
        metadataColorSolid={metadataColorSolid}
        metadataColorStartGradient={metadataColorStartGradient}
        metadataColorEndGradient={metadataColorEndGradient}
        metadataColorGradientDirection={metadataColorGradientDirection}
        // footer area
        footerAreaTypography={actionHelpTypo as TypographyValue}
        footerAreaAlignment={actionAlign as any}
        footerAreaLimit={textLine6Limit}
        footerMargin={footerMargin}
        footerAreaColorType={footerAreaColor}
        footerAreaColorSolid={footerAreaColorSolid}
        footerAreaColorStartGradient={footerAreaColorStartGradient}
        footerAreaColorEndGradient={footerAreaColorEndGradient}
        footerAreaColorGradientDirection={footerAreaColorGradientDirection}
        metadata={{}}
        cornerRadius={itemShape as CornerRadius}
        equalHeight={equalHeight}
        headerDropdownItems={renderMenuItems(data, 'header')}
        {...(!!embed
          ? {
              media: embed,
            }
          : {})}
        leading={{
          enabled: !!footerLeadingActionEnabled,
          text: leadingLabel,
          typography: footerLeadingTypography as TypographyValue,
          colorVariant: leadingButtonColor,
          shape: leadingShape,
          size: leadingButtonFullWidth ? 'lg' : 'lg',
          fullWidth: leadingButtonFullWidth,
          disabled: !footerLeadingActionEnabled,
          iconPosition: footerLeadingActionIconPosition,
          icon: <MaterialIcon icon={footerLeading?.icon ?? ''} size={24} />,
          padding: leadingButtonPadding,
          onClick: () => {
            simulateAction({
              actions: footerLeadingActions,
              rowIndex,
              rowId: data._id,
              tableId,
            })
          },
        }}
        footerTitle={actionHelp}
        footerTitleTypography={actionHelpTypo}
        footerTitleAlignment={actionAlign}
        trailing={{
          colorVariant: trailingButtonColor,
          text: trailingLabel,
          icon: <MaterialIcon icon={footerTrailing?.icon ?? ''} size={24} />,
          enabled: !!footerTrailingActionEnabled,
          typography: footerTrailingTypography as TypographyValue,
          shape: trailingShape,
          size: trailingButtonFullWidth ? 'lg' : 'lg',
          fullWidth: trailingButtonFullWidth,
          disabled: !footerTrailingActionEnabled,
          iconPosition: footerTrailingActionIconPosition,
          padding: trailingButtonPadding,
          onClick: () => {
            simulateAction({
              actions: footerTrailingActions,
              rowIndex,
              rowId: data._id,
              tableId,
            })
          },
        }}
        footerDropdownItems={renderMenuItems(data, 'trailing')}
        footerButtonsSpacing={footerGap as any}
        aspectRatio={mediaRatio ?? '1:1'}
        showImage={!!mediaCol}
        padding={padding}
        card={{
          avatar: getAvatar()?.value as string,
          avatarType: getAvatar()?.type as 'image' | 'icon',
          avatarColor: getFinalThumbIconColor(data) ?? thumbIconColorVariant,
          avatarBackgroundColor: getFinalThumbIconBackgroundColor(data) ?? thumbIconBackgroundColorVariant,
          isAvatarDynamicColor: isThumbIconColorDynamic,
          avatarShape: headerAvatarShape,
          avatarVariant: thumbIconVariant,
          description: descriptionContent,
          title: headerContent,
          contentTitle: titleContent,
          contentSubtitle: subtitleContent,
          contentSupportingText: supportingTextContent,
          metadata: renderMetaData(),
          mediaType,
          ...(!embed
            ? {
                image:
                  mediaType === MediaType.Image ? (
                    <div style={{ ...mediaMargin } as CSSProperties}>
                      <img
                        src={mediaSrc || THUMB_SRC}
                        className='w-full h-full object-cover shrink-0'
                        onError={(e) => (e.currentTarget.src = THUMB_SRC)}
                        style={{
                          clipPath: `inset(0 0 0 0 round ${getBorderRadius(mediaCorner)})`,
                          aspectRatio:
                            mediaRatio && mediaRatio !== AspectRatio.Auto ? mediaRatio.replace(':', ' / ') : undefined,
                        }}
                      />
                    </div>
                  ) : mediaType === MediaType.Icon ? (
                    <div
                      className={cn(
                        'flex items-center h-full w-fit p-4 rounded-full',
                        mediaAlignment === 'center'
                          ? 'justify-self-center'
                          : mediaAlignment === 'end'
                            ? 'justify-self-end'
                            : 'justify-self-start'
                      )}
                      style={{
                        ...getIconStyleByType(
                          mediaIconVariant,
                          getFinalMediaIconColor(data) ?? mediaIconColorVariant,
                          getFinalMediaIconBackgroundColor(data) ?? mediaIconBackgroundColorVariant
                          // mediaIconBackgroundColorVariant ?? backgroundColorPage
                        ),
                        ...mediaMargin,
                        clipPath: `inset(0 0 0 0 round ${getBorderRadius(mediaCorner)})`,
                      }}
                    >
                      <MaterialIcon
                        icon={(media as string) || 'star'}
                        size={mediaIconSize}
                        fill={mediaIconVariant === 'filled'}
                        className={cn(
                          'material-symbols-rounded',
                          mediaIconVariant === 'outlined' ? 'material-symbols' : '',
                          mediaAlignment === 'center' ? 'mx-auto' : mediaAlignment === 'end' ? 'mr-auto' : 'ml-auto'
                        )}
                      />
                    </div>
                  ) : undefined,
              }
            : {}),
        }}
        colorGeneration={{
          mode: colorMode,
          seed: `${'seed-card-item'}-${data?._id}`,
          startIndex: 0,
          excludeColors: [],
          toneOrder: colorToneOrder,
          startPalette: colorStartPalette,
        }}
        itemIndex={rowIndex}
        hoverEffect={hoverEffect}
        hoverIntensity='md'
        componentSpacing={settings.componentSpacing}
      />
    )
  }
)
CompCardItemCore.displayName = 'CompCardItemCore'
